import { MapPin, Mail, Phone, Linkedin } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import ContactForm from "@/components/ContactForm";
import OfficeMap from "@/components/OfficeMap";
import HEROES from "@/lib/pageHeroes.json";


function InfoCard({ testid, icon: Icon, label, value, href, action, delay }) {
  const Tag = href ? "a" : "div";
  const props = href ? { href, target: href.startsWith("http") ? "_blank" : undefined, rel: "noreferrer" } : {};
  return (
    <Reveal delay={delay}>
      <Tag
        {...props}
        data-testid={testid}
        className="group flex h-full items-start gap-5 border border-border bg-card/40 p-7 card-lift"
        style={{ borderRadius: "var(--radius)" }}
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="eyebrow mb-2">{label}</p>
          <p className="break-words text-foreground/85 leading-relaxed">{value}</p>
          {action && (
            <span className="mt-2 inline-block text-xs text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              {action}
            </span>
          )}
        </div>
      </Tag>
    </Reveal>
  );
}

export default function Contact() {
  const { t, L } = useLang();
  const site = useSite();
  const s = site?.settings || {};
  const offices = site?.offices || [];
  const responseNote = L(s.response_time);

  return (
    <div data-testid="contact-page">
      <Seo page="contact" />
      <PageHero eyebrow={t("contact_eyebrow")} title={t("contact_title")} sub={t("contact_sub")} image={HEROES["contact"]} />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28 grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <ContactForm responseNote={responseNote} />
        </Reveal>

        <div className="lg:col-span-5 grid content-start gap-5 sm:grid-cols-2 lg:grid-cols-1">
          <InfoCard testid="contact-hq-card" icon={MapPin} label={t("address_label")} value={s.address} delay={0.1} />
          {s.phone && (
            <InfoCard
              testid="contact-phone-card"
              icon={Phone}
              label={t("phone_label")}
              value={s.phone}
              href={`tel:${s.phone.replace(/\s/g, "")}`}
              action={t("contact_call")}
              delay={0.16}
            />
          )}
          {s.email && (
            <InfoCard
              testid="contact-email-card"
              icon={Mail}
              label={t("email_label")}
              value={s.email}
              href={`mailto:${s.email}`}
              action={t("contact_email_us")}
              delay={0.22}
            />
          )}
          {s.linkedin && (
            <InfoCard
              testid="contact-linkedin-card"
              icon={Linkedin}
              label="LinkedIn"
              value={L(s.site_name)}
              href={s.linkedin}
              action={t("leader_profile")}
              delay={0.28}
            />
          )}
        </div>
      </section>

      {offices.length > 0 && (
        <section className="border-t border-border bg-card/25">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28">
            <OfficeMap offices={offices} />
          </div>
        </section>
      )}
    </div>
  );
}
